import { Suspense, useState } from 'react'
import type { FormEvent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useFetchUncachedCb } from '@gluedigital/ruse-fetch-extras'
import { useDialog } from '@hooks/useDialog'
import { useCompanyTags } from '@hooks/useCompanyTags'
import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary'
import TagsInput from '@components/TagsInput/TagsInput'
import Toast from '@components/Toast/Toast'
import { ADMIN_URL } from 'src/constants/global'
import type { Company } from 'src/types/global'

import './CompanyForm.css'

interface CompanyFormProps {
  onClose: () => void
  company?: Company
  onSaved: (company: Company) => void
}

interface CompanyResponse {
  ok: boolean
  company: Company
  error?: string
}

const TAGS_INPUT_ID = 'company-form-tags'

interface TagsFieldProps {
  values: string[]
  onChange: (values: string[]) => void
  placeholder: string
}

const TagsFieldFallback = ({
  values,
  onChange,
  placeholder
}: TagsFieldProps) => (
  <TagsInput
    id={TAGS_INPUT_ID}
    values={values}
    onChange={onChange}
    placeholder={placeholder}
  />
)

const TagsFieldWithSuggestions = (props: TagsFieldProps) => {
  const suggestions = useCompanyTags()
  return <TagsInput id={TAGS_INPUT_ID} {...props} suggestions={suggestions} />
}

export default function CompanyForm({
  onClose,
  company,
  onSaved
}: CompanyFormProps) {
  const intl = useIntl()
  const { dialogRef } = useDialog(true, onClose)
  const doSubmit = useFetchUncachedCb()
  const isEdit = !!company

  const [nif, setNif] = useState(company?.nif ?? '')
  const [name, setName] = useState(company?.name ?? '')
  const [tags, setTags] = useState<string[]>(company?.tags ?? [])
  const [editReason, setEditReason] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim() || (!isEdit && !nif.trim())) return
    if (isEdit && !editReason.trim()) return

    setIsSubmitting(true)
    setError('')
    try {
      const response = await doSubmit<CompanyResponse>(
        isEdit
          ? `${ADMIN_URL}/companies/${company.id}`
          : `${ADMIN_URL}/companies`,
        {
          method: isEdit ? 'PUT' : 'POST',
          body: isEdit
            ? { name, tags, reason: editReason.trim() }
            : { nif, name, tags }
        }
      )
      onSaved(response.company)
      onClose()
    } catch (err) {
      const payload = (err as { payload?: { error?: string } })?.payload
      setError(
        payload?.error ||
          intl.formatMessage({ id: 'company-form.error.generic' })
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const tagsPlaceholder = intl.formatMessage({
    id: 'company-form.tags-placeholder'
  })

  return (
    <dialog
      id="company-form-dialog"
      className="app-dialog"
      ref={dialogRef}
      onCancel={onClose}
    >
      <form className="dialog-inner" onSubmit={handleSubmit}>
        <h3>
          <FormattedMessage
            id={
              isEdit ? 'company-form.title.edit' : 'company-form.title.create'
            }
          />
        </h3>
        <label htmlFor="company-form-nif">
          <strong>
            <FormattedMessage id="company-form.nif" />
          </strong>
          <input
            id="company-form-nif"
            required
            type="text"
            value={nif}
            disabled={isEdit}
            onChange={(e) => setNif(e.target.value)}
          />
        </label>
        <label htmlFor="company-form-name">
          <strong>
            <FormattedMessage id="company-form.name" />
          </strong>
          <input
            id="company-form-name"
            required
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label htmlFor={TAGS_INPUT_ID}>
          <strong>
            <FormattedMessage id="company-form.tags" />
          </strong>
          <ErrorBoundary
            errorComponent={() => (
              <TagsFieldFallback
                values={tags}
                onChange={setTags}
                placeholder={tagsPlaceholder}
              />
            )}
          >
            <Suspense
              fallback={
                <TagsFieldFallback
                  values={tags}
                  onChange={setTags}
                  placeholder={tagsPlaceholder}
                />
              }
            >
              <TagsFieldWithSuggestions
                values={tags}
                onChange={setTags}
                placeholder={tagsPlaceholder}
              />
            </Suspense>
          </ErrorBoundary>
        </label>
        {isEdit && (
          <label htmlFor="company-form-edit-reason">
            <strong>
              <FormattedMessage id="company-form.edit-reason" />
            </strong>
            <textarea
              id="company-form-edit-reason"
              rows={3}
              required
              value={editReason}
              disabled={isSubmitting}
              placeholder={intl.formatMessage({
                id: 'company-form.edit-reason-placeholder'
              })}
              onChange={(e) => setEditReason(e.target.value)}
            />
          </label>
        )}
        <div className="dialog-actions">
          <button type="button" onClick={onClose} disabled={isSubmitting}>
            <FormattedMessage id="cancel" />
          </button>
          <button
            type="submit"
            className="highlight-button"
            disabled={isSubmitting}
          >
            <FormattedMessage id="save" />
          </button>
        </div>
      </form>
      {error && <Toast message={error} onClose={() => setError('')} />}
    </dialog>
  )
}
